import {
  Control,
  FieldValues,
  FieldErrors,
  FieldPath,
  FieldArray,
  ArrayPath,
} from 'react-hook-form';

// Types
import type { TechStackItemFormValues } from '@/schemas';
import { ProjectStack } from '@shared/types';

/**
 * Check if there are any form errors
 */
export const hasFormErrors = (errors: FieldErrors): boolean =>
  Object.keys(errors ?? {}).length > 0;

/**
 * Safely converts a typed Control to FieldValues Control
 * This maintains type safety while allowing component reusability
 */
export const asFieldValuesControl = <T extends FieldValues>(
  control: Control<T>,
): Control<FieldValues> => {
  return control as Control<FieldValues>;
};

/**
 * Type-safe field name converter for nested form paths
 */
export const asFieldName = (fieldPath: string): string => {
  return fieldPath;
};

/**
 * Filters out tech stacks that have both an empty category and no non-empty items.
 * @param techStacks - Array of technical stack objects to filter.
 * @returns A new array containing only non-empty tech stacks.
 */
export const filterNonEmptyTechStacks = (
  techStacks: TechStackItemFormValues[],
): TechStackItemFormValues[] =>
  techStacks.filter(({ name, items = [] }) => {
    const hasName = (name?.trim() ?? '') !== '';
    const hasItems = items.some((item) => item.trim() !== '');

    return hasName || hasItems;
  });

/**
 * Filters valid tech stacks (stacks with non-empty names AND non-empty items)
 * @param stacks - Array of tech stack objects to filter
 * @returns Array of valid tech stacks only (with both name and items)
 */
export const getValidStacks = (stacks: ProjectStack[]) =>
  stacks.filter(({ name, items }) => Boolean(name?.trim() && items?.length));

/**
 * Type-safe helper to create ArrayPath from FieldPath
 * @param fieldName - The field path to convert
 * @returns ArrayPath for useFieldArray
 */
export const createArrayPath = <T extends FieldValues>(
  fieldName: FieldPath<T>,
): ArrayPath<T> => fieldName as unknown as ArrayPath<T>;

/**
 * Type-safe helper to create FieldArray from default values
 * @param defaultValues - The default values to convert
 * @returns FieldArray for useFieldArray append
 */
export const createFieldArray = <T extends FieldValues>(defaultValues: {
  name: string;
  items: string[];
}): FieldArray<T, ArrayPath<T>> =>
  defaultValues as unknown as FieldArray<T, ArrayPath<T>>;
